/**
 * Phase 8 base story — Subject Detail "Visits & eSource" tab (AC1–AC12).
 *
 * Route: /subjects/:id/visits-esource
 *
 * Two-panel layout:
 *   - Left: chronological subject visit timeline with status pills
 *     (Completed / In Progress / Scheduled / Locked). Active visit is
 *     highlighted (AC1, AC2).
 *   - Right: for the active visit, all the surfaces we already
 *     built for VisitDetailPage — schedule banners, IE checklist,
 *     lifecycle (sign / reopen), eSource forms, documents, AE prompt.
 *
 * We deliberately compose the existing components rather than
 * duplicating render code — the equivalent surfaces already work on
 * /visits/:id and stay in lockstep.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarCheck2,
  Clock,
  Loader2,
  Lock,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  subjects,
  type VisitRow,
  type VisitLockState,
} from '@/api/client';
import { resolveId } from '@/lib/encryptId';
import { fmtDate, visitActualDateLabel, visitWhen } from '@/lib/dates';

import { OpenAdverseEventsPrompt } from '@/pages/visits/OpenAdverseEventsPrompt';
import { VisitFormsSection } from '@/pages/visits/VisitFormsSection';
import { VisitLifecyclePanel } from '@/pages/visits/VisitLifecyclePanel';
import { VisitDocumentsPanel } from '@/pages/visits/VisitDocumentsPanel';
import { VisitIeChecklistPanel } from '@/pages/visits/VisitIeChecklistPanel';
import {
  NextVisitBanner,
  OutOfWindowBanner,
} from '@/pages/visits/VisitScheduleBanners';
import { subjectDisplayId } from '@/lib/subjectLabel';

// ---------------------------------------------------------------------------

type VisitStatusBadge =
  | 'planned'
  | 'completed'
  | 'signed_pending_sdv'
  | 'locked'
  | 'missed'
  | 'cancelled';

const STATUS_LABEL: Record<VisitStatusBadge, string> = {
  planned: 'Scheduled',
  completed: 'Completed',
  signed_pending_sdv: 'Signed — pending SDV',
  locked: 'Locked',
  missed: 'Missed',
  cancelled: 'Cancelled',
};

const STATUS_BADGE: Record<VisitStatusBadge, string> = {
  planned: 'bg-slate-100 text-slate-700 border-slate-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  signed_pending_sdv: 'bg-amber-50 text-amber-700 border-amber-200',
  locked: 'bg-brand-50 text-brand-700 border-brand-200',
  missed: 'bg-danger-50 text-danger-700 border-danger-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

function resolveStatus(v: VisitRow): VisitStatusBadge {
  const lockState = (v.lock_state ?? 'unlocked') as VisitLockState;
  if (lockState === 'locked') return 'locked';
  if (lockState === 'signed_pending_sdv') return 'signed_pending_sdv';
  if (v.status === 'missed') return 'missed';
  if (v.status === 'cancelled') return 'cancelled';
  if (v.status === 'completed' || v.completed_at) return 'completed';
  return 'planned';
}

function statusIcon(s: VisitStatusBadge) {
  switch (s) {
    case 'completed':
      return CalendarCheck2;
    case 'signed_pending_sdv':
      return RefreshCw;
    case 'locked':
      return Lock;
    default:
      return Clock;
  }
}

// ---------------------------------------------------------------------------

export function SubjectVisitsEsourcePage() {
  const { id: encryptedParam } = useParams<{ id: string }>();
  const subjectId = encryptedParam ? resolveId(encryptedParam) : undefined;

  const subjectQ = useQuery({
    queryKey: ['subject', subjectId],
    queryFn: () => subjects.detail(subjectId!),
    enabled: !!subjectId,
  });

  const visits = useMemo<VisitRow[]>(
    () => subjectQ.data?.visits ?? [],
    [subjectQ.data],
  );
  const orderedVisits = useMemo(
    () =>
      [...visits].sort((a, b) => {
        const da = visitWhen(a) ?? '';
        const db = visitWhen(b) ?? '';
        return da.localeCompare(db);
      }),
    [visits],
  );

  const [activeVisitId, setActiveVisitId] = useState<string | null>(null);
  // Default the active visit to: signed-pending-SDV → planned →
  // last completed → first. Runs once when visits arrive.
  useEffect(() => {
    if (activeVisitId || orderedVisits.length === 0) return;
    const pending = orderedVisits.find(
      (v) => resolveStatus(v) === 'signed_pending_sdv',
    );
    const planned = orderedVisits.find((v) => resolveStatus(v) === 'planned');
    const lastCompleted = [...orderedVisits]
      .reverse()
      .find((v) => resolveStatus(v) === 'completed');
    const first = pending ?? planned ?? lastCompleted ?? orderedVisits[0];
    if (first) setActiveVisitId(first.visit_id);
  }, [orderedVisits, activeVisitId]);

  if (!subjectId) {
    return (
      <div className="p-10 text-center text-sm text-danger-600">
        Subject id required.
      </div>
    );
  }

  const subject = subjectQ.data?.subject;
  const activeVisit = orderedVisits.find((v) => v.visit_id === activeVisitId) ?? null;

  return (
    <div className="space-y-3">
      <PageHeader
        backTo={`/subjects/${encryptedParam}`}
        title={
          subject
            ? `Visits & eSource — Subject ${subjectDisplayId(subject)}`
            : 'Visits & eSource'
        }
        subtitle="Visit timeline on the left; select any visit to open its data-entry surfaces on the right."
      />

      {/* Subject-scoped next-visit banner sits above the panels so it
          renders whether or not a visit is selected. */}
      <NextVisitBanner subjectId={subjectId} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
        {/* Left: visit timeline */}
        <aside className="card p-3">
          <h2 className="mb-2 text-xs font-semibold text-ink-primary">
            Visit timeline
            <span className="ml-1 rounded bg-surface-muted/50 px-1.5 py-0.5 text-[10px] font-normal">
              {orderedVisits.length}
            </span>
          </h2>
          {subjectQ.isLoading ? (
            <div className="p-4 text-center text-[11px] text-ink-muted">
              <Loader2 className="mx-auto h-3 w-3 animate-spin" />
            </div>
          ) : orderedVisits.length === 0 ? (
            <p className="rounded border border-dashed border-surface-border p-3 text-center text-[11px] text-ink-muted">
              No visits scheduled for this subject yet.
            </p>
          ) : (
            <ol className="space-y-1">
              {orderedVisits.map((v) => {
                const s = resolveStatus(v);
                const Icon = statusIcon(s);
                const isActive = v.visit_id === activeVisitId;
                const isUnscheduled =
                  v.visit_type === 'unscheduled' || v.unscheduled === true;
                const planned = visitWhen(v);
                const actual = visitActualDateLabel(v);
                return (
                  <li key={v.visit_id}>
                    <button
                      type="button"
                      onClick={() => setActiveVisitId(v.visit_id)}
                      className={`w-full rounded border p-2 text-left text-xs hover:bg-surface-muted/40 ${
                        isActive
                          ? 'border-brand-400 bg-brand-50/50 ring-2 ring-brand-300'
                          : 'border-surface-border bg-surface-card'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-ink-primary">
                            {v.visit_name ?? v.visit_label ?? 'Visit'}
                            {isUnscheduled && (
                              <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700">
                                unscheduled
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[10px] text-ink-muted">
                            Planned {planned ? fmtDate(planned) : '—'}
                            {' · '}
                            <span className={actual.recorded ? 'font-medium text-ink-secondary' : ''}>
                              {actual.recorded ? `Actual ${actual.text}` : actual.text}
                            </span>
                            {v.flagged_deviation === 'true' && (
                              <span className="ml-1 rounded bg-danger-100 px-1 py-0.5 text-[9px] font-medium text-danger-700">
                                deviation
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-medium ${STATUS_BADGE[s]}`}
                        >
                          <Icon className="h-2.5 w-2.5" />
                          {STATUS_LABEL[s]}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </aside>

        {/* Right: active-visit surfaces (composes the same components used on VisitDetailPage). */}
        <div className="min-w-0 space-y-3">
          {!activeVisit ? (
            <div className="card p-10 text-center text-xs text-ink-muted">
              <ShieldCheck className="mx-auto mb-2 h-5 w-5 text-brand-500" />
              Pick a visit on the left to open its data-entry surface.
            </div>
          ) : (
            <ActiveVisitPanels visit={activeVisit} subjectId={subjectId} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ActiveVisitPanels({
  visit,
  subjectId,
}: {
  visit: VisitRow;
  subjectId: string;
}) {
  return (
    <>
      {/* Same banner components that render on the standalone visit detail. */}
      <OutOfWindowBanner visit={visit} />
      {/* Open-AE prompt hides itself when there's nothing to review. */}
      <OpenAdverseEventsPrompt visitId={visit.visit_id} />

      {/* PI sign / reopen surface — extended-contract AC1-14. */}
      <VisitLifecyclePanel visitId={visit.visit_id} />

      {/* I/E checklist self-hides when the visit template has no criteria. */}
      <VisitIeChecklistPanel visitId={visit.visit_id} />

      {/* eSource forms — Phase 7A runtime. */}
      {visit.subject_id && visit.study_id && (
        <VisitFormsSection
          subjectId={visit.subject_id}
          visitId={visit.visit_id}
          studyId={visit.study_id}
        />
      )}

      {/* Per-visit documents — AC18. */}
      <VisitDocumentsPanel visitId={visit.visit_id} />

      {/* Deep-link back to the standalone visit page for anything not
          composed here (e.g. legacy metadata cards). */}
      <p className="text-[10px] text-ink-muted">
        Subject {subjectId.slice(0, 8)}… · visit {visit.visit_id.slice(0, 8)}…
      </p>
    </>
  );
}
